import React, { useState, useEffect } from 'react';
import { Category, Ticket } from '../../types';
import apiService from '../../services/api';
import CountrySelect from '../common/CountrySelect';
import CompanySuggestion from '../common/CompanySuggestion';
import DeviceSelector from '../common/DeviceSelector';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface CreateTicketProps {
  onBack: () => void;
  onTicketCreated: () => void;
}

interface TicketFormData {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerCompany: string;
  customerAddress: string; // Keep for backward compatibility
  customerStreetAddress: string;
  customerCity: string;
  customerState: string;
  customerZipCode: string;
  customerCountry: string;
  customerType: string;
  deviceModel: string;
  deviceSerialNumber: string;
}

const CreateTicket: React.FC<CreateTicketProps> = ({ onBack, onTicketCreated }) => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCompany: '',
    customerAddress: '', // Keep for backward compatibility
    customerStreetAddress: '',
    customerCity: '',
    customerState: '',
    customerZipCode: '',
    customerCountry: '',
    customerType: '',
    deviceModel: '',
    deviceSerialNumber: '',
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);
  const [deviceModels, setDeviceModels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Company autocomplete state
  const [companySuggestions, setCompanySuggestions] = useState<Array<{ name: string; confidence: number; description?: string; id?: string }>>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companyInputTimeout, setCompanyInputTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Device selection state
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);

  useEffect(() => {
    loadCategories();
    loadDropdownOptions();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }
    };
  }, [companyInputTimeout]);

  const loadCategories = async () => {
    try {
      // Use the dropdown options API to get categories configured in system settings
      const dropdownOptions = await apiService.getDropdownOptions();
      const activeCategories = dropdownOptions.categories.filter(cat => (cat as any).isActive !== false);
      setCategories(activeCategories);
      // Don't auto-select the first category - let users choose
    } catch (error) {
      console.error('Error loading categories from dropdown options:', error);
      // Fallback to original method if dropdown options fail
      try {
        const fetchedCategories = await apiService.getCategories();
        setCategories(fetchedCategories);
        // Don't auto-select the first category - let users choose
      } catch (fallbackError) {
        console.error('Error loading categories (fallback):', fallbackError);
      }
    }
  };

  const loadDropdownOptions = async () => {
    try {
      const dropdownOptions = await apiService.getDropdownOptions();
      
      // Load customer types and device models from dropdown options
      const activeCustomerTypes = dropdownOptions.customerTypes.filter(type => type.isActive !== false);
      const activeDeviceModels = dropdownOptions.deviceModels.filter(model => model.isActive !== false);
      
      setCustomerTypes(activeCustomerTypes);
      setDeviceModels(activeDeviceModels);
    } catch (error) {
      console.error('Error loading dropdown options:', error);
      // Set default fallback options if API fails
      setCustomerTypes([
        { id: 'standard', name: 'Standard' },
        { id: 'vip', name: 'VIP' },
        { id: 'distributor', name: 'Distributor' }
      ]);
      setDeviceModels([
        { id: 'bwiii', name: 'BWIII' },
        { id: 'bwmini', name: 'BWMini' },
        { id: 'compass', name: 'Compass' },
        { id: 'maxxi', name: 'Maxxi' }
      ]);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, customerEmail: email }));
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in both title and description');
      return;
    }

    if (!formData.customerName.trim() || !formData.customerEmail.trim()) {
      setError('Please provide your name and email address');
      return;
    }

    // Validate email format
    if (!validateEmail(formData.customerEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      console.log('Creating ticket with form data:', formData);
      
      console.log('🔧 About to send deviceModel:', formData.deviceModel);
      console.log('🔧 deviceModel type:', typeof formData.deviceModel);
      console.log('🔧 Sending device model as-is without mapping to preserve configurable models');
      
      // Transform formData to match backend expectations
      const ticketData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        categoryId: formData.category || null, // Convert empty string to null for UUID compatibility
        customerInfo: {
          name: formData.customerName,
          email: formData.customerEmail,
          phone: formData.customerPhone,
          company: formData.customerCompany,
          address: formData.customerAddress, // Keep for backward compatibility
          streetAddress: formData.customerStreetAddress,
          city: formData.customerCity,
          state: formData.customerState,
          zipCode: formData.customerZipCode,
          country: formData.customerCountry,
          customerType: formData.customerType,
          deviceModel: formData.deviceModel, // Use original model from form
          deviceSerialNumber: formData.deviceSerialNumber,
          selectedDeviceId: selectedDevice?.id || null // Pass selected device ID if one is chosen, null otherwise
        }
      };
      
      console.log('Sending ticket data to API:', ticketData);
      console.log('🔧 CategoryId value:', ticketData.categoryId, 'type:', typeof ticketData.categoryId);
      console.log('🔧 CustomerInfo selectedDeviceId:', ticketData.customerInfo.selectedDeviceId, 'type:', typeof ticketData.customerInfo.selectedDeviceId);
      const createdTicket = await apiService.createTicket(ticketData);
      console.log('🔧 Created ticket response:', createdTicket);
      console.log('🔧 Created ticket deviceModel:', createdTicket?.deviceModel);
      console.log('🔧 Created ticket deviceSerialNumber:', createdTicket?.deviceSerialNumber);
      onTicketCreated();
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' ? value as 'low' | 'medium' | 'high' : value,
    }));

    // Handle company field changes for autocomplete
    if (name === 'customerCompany') {
      // Clear existing timeout
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }

      if (value.trim().length >= 2) {
        // Set a debounced timeout to fetch suggestions
        const timeout = setTimeout(async () => {
          try {
            const response = await apiService.fuzzyMatchCompany(value.trim(), 75);
            if (response.matches.length > 0) {
              // Transform matches to match CompanySuggestion component expectations
              const suggestions = response.matches.map(match => ({
                name: match.name,
                confidence: match.score,
                description: match.aliases?.length > 0 ? `Also known as: ${match.aliases.join(', ')}` : undefined,
                id: match.id
              }));
              setCompanySuggestions(suggestions);
              setShowCompanySuggestions(true);
            } else {
              setShowCompanySuggestions(false);
              // Clear company selection if no matches
              setSelectedCompanyId(null);
            }
          } catch (error) {
            console.error('Error fetching company suggestions:', error);
            setShowCompanySuggestions(false);
          }
        }, 500); // 500ms delay

        setCompanyInputTimeout(timeout);
      } else {
        setShowCompanySuggestions(false);
      }
    }

    // Validate email
    if (name === 'customerEmail') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
  };

  const handleCompanySelect = (companyName: string) => {
    setFormData(prev => ({
      ...prev,
      customerCompany: companyName
    }));
    setShowCompanySuggestions(false);
    
    // Find the selected company ID
    const selectedSuggestion = companySuggestions.find(suggestion => suggestion.name === companyName);
    if (selectedSuggestion && selectedSuggestion.id) {
      setSelectedCompanyId(selectedSuggestion.id);
    }
  };

  const handleDeviceSelect = (device: any) => {
    console.log('🔧 handleDeviceSelect called with device:', device);
    setSelectedDevice(device);
    if (device) {
      // Update form data with device information
      // device.model is already mapped to valid enum value by DeviceSelector
      console.log('🔧 Setting deviceModel to:', device.model);
      console.log('🔧 Setting deviceSerialNumber to:', device.serialNumber);
      console.log('🔧 Current deviceModels options:', deviceModels);
      setFormData(prev => ({
        ...prev,
        deviceModel: device.model,
        deviceSerialNumber: device.serialNumber
      }));
    } else {
      // Clear device information
      setFormData(prev => ({
        ...prev,
        deviceModel: '',
        deviceSerialNumber: ''
      }));
    }
  };

  const handleDismissCompanySuggestions = () => {
    setShowCompanySuggestions(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to tickets
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Ticket</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Describe your issue and we'll get back to you as soon as possible.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              placeholder="Brief description of your issue"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              rows={6}
              required
              value={formData.description}
              onChange={handleChange}
              placeholder="Please provide detailed information about your issue..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Customer Information */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  required
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="customerEmail"
                  name="customerEmail"
                  required
                  value={formData.customerEmail}
                  onChange={handleEmailChange}
                  placeholder="your.email@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                    emailError ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{emailError}</p>
                )}
              </div>

              <div>
                <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="customerPhone"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="customerCompany" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="customerCompany"
                    name="customerCompany"
                    value={formData.customerCompany}
                    onChange={handleChange}
                    placeholder="Your company name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                  {showCompanySuggestions && (
                    <CompanySuggestion
                      suggestions={companySuggestions}
                      onSelect={handleCompanySelect}
                      onDismiss={handleDismissCompanySuggestions}
                    />
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="customerType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Customer Type
                </label>
                <select
                  id="customerType"
                  name="customerType"
                  value={formData.customerType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select customer type...</option>
                  {customerTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                  {/* Fallback options if no dropdown options are loaded */}
                  {customerTypes.length === 0 && (
                    <>
                      <option value="Standard">Standard</option>
                      <option value="VIP">VIP</option>
                      <option value="Distributor">Distributor</option>
                    </>
                  )}
                </select>
              </div>

            </div>

            {/* Device Information */}
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Device Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="deviceModel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Device Model
                  </label>
                  <select
                    id="deviceModel"
                    name="deviceModel"
                    value={formData.deviceModel}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select device model...</option>
                    {deviceModels.map((model) => (
                      <option key={model.id} value={model.name}>
                        {model.name}
                      </option>
                    ))}
                    {/* Fallback options if no dropdown options are loaded */}
                    {deviceModels.length === 0 && (
                      <>
                        <option value="BWIII">BWIII</option>
                        <option value="BWMini">BWMini</option>
                        <option value="Compass">Compass</option>
                        <option value="Maxxi">Maxxi</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label htmlFor="deviceSerialNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Device Serial Number
                  </label>
                  <input
                    type="text"
                    id="deviceSerialNumber"
                    name="deviceSerialNumber"
                    value={formData.deviceSerialNumber}
                    onChange={handleChange}
                    placeholder="Enter serial number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
              
              {/* Device Selection from Company */}
              <DeviceSelector
                companyId={selectedCompanyId}
                selectedDeviceId={selectedDevice?.id || null}
                onDeviceSelect={handleDeviceSelect}
                loading={loading}
              />
            </div>

            {/* Address Fields */}
            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="customerStreetAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  id="customerStreetAddress"
                  name="customerStreetAddress"
                  value={formData.customerStreetAddress}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="customerCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    id="customerCity"
                    name="customerCity"
                    value={formData.customerCity}
                    onChange={handleChange}
                    placeholder="Los Angeles"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <label htmlFor="customerState" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    id="customerState"
                    name="customerState"
                    value={formData.customerState}
                    onChange={handleChange}
                    placeholder="California"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <label htmlFor="customerZipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zip/Postal Code
                  </label>
                  <input
                    type="text"
                    id="customerZipCode"
                    name="customerZipCode"
                    value={formData.customerZipCode}
                    onChange={handleChange}
                    placeholder="90210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="customerCountry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <CountrySelect
                  id="customerCountry"
                  name="customerCountry"
                  value={formData.customerCountry}
                  onChange={(value) => setFormData(prev => ({ ...prev, customerCountry: value }))}
                  placeholder="Select your country..."
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || emailError !== ''}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Ticket'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicket; 